import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { permissionMatches, useAuth, usePermission } from "@ncp/api-client";
import { useCatalog, type AssistantCatalog, type AssistantSummary, type WorkspaceSummary } from "@ncp/assistant-sdk";
import type { PaletteItem } from "@ncp/ui";

const WORKSPACE_STORAGE_KEY = "ncp.assistant.workspace";

export interface WorkspaceContextValue {
  catalog: AssistantCatalog | undefined;
  loading: boolean;
  error: Error | null;
  /** The selected workspace (validated against the catalog; falls back to the server default, then the first membership). */
  workspace: WorkspaceSummary | null;
  workspaceKey: string | null;
  setWorkspaceKey: (key: string) => void;
  workspaces: WorkspaceSummary[];
  /** Assistants of the selected workspace; the default one first. */
  assistants: AssistantSummary[];
  defaultAssistant: AssistantSummary | null;
  /** True when the caller holds the `assistant.*` / `knowledge.*` permission (session or catalog-resolved). */
  can: (permission: string | null | undefined) => boolean;
  offline: boolean;
  // ---- shell chrome
  openPalette: () => void;
  openShortcuts: () => void;
  /** Pages contribute contextual actions to the command palette while mounted. */
  registerActions: (actions: PaletteItem[]) => () => void;
  actions: PaletteItem[];
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

function readStored(storageKey: string): string | null {
  try { return localStorage.getItem(storageKey); } catch { return null; }
}

export function WorkspaceProvider({ children, onOpenPalette, onOpenShortcuts }: { children: ReactNode; onOpenPalette: () => void; onOpenShortcuts: () => void }) {
  const { permissions } = usePermission();
  // The workspaces and assistants exist for people with the AI workspace; a content-blind DT Administrator never asks (no 403 in the console).
  const catalog = useCatalog({ enabled: permissionMatches(permissions, "assistant.use") });
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // Remembered per person: whoever signs in next on a shared machine starts from their own default, not the last user's workspace.
  const storageKey = `${WORKSPACE_STORAGE_KEY}:${user?.id ?? "anonymous"}`;
  const [storedFor, setStoredFor] = useState(storageKey);
  const [stored, setStored] = useState<string | null>(() => readStored(storageKey));
  if (storedFor !== storageKey) {
    setStoredFor(storageKey);
    setStored(readStored(storageKey));
  }
  const [actionSets, setActionSets] = useState<PaletteItem[][]>([]);

  const workspaces = catalog.data?.workspaces ?? [];
  // `?ws=` wins (deep links from other surfaces), then the remembered choice, then the server's default.
  const requested = params.get("ws") ?? stored ?? catalog.data?.defaultWorkspaceKey ?? null;
  const workspace = useMemo(() => workspaces.find((w) => w.key === requested) ?? workspaces.find((w) => w.key === catalog.data?.defaultWorkspaceKey) ?? workspaces[0] ?? null, [workspaces, requested, catalog.data?.defaultWorkspaceKey]);

  // The latest choice, ahead of the render it causes: a screen's effect may pick a space (`setWorkspaceKey`) in the same commit in which
  // the effect below would persist the space this render resolved; that older value must not overwrite the newer choice.
  const chosen = useRef(stored);
  chosen.current = stored;
  useEffect(() => {
    // Nothing chosen on this device: the server's default stays in force (it follows pref:ui.department, else the first department the
    // account was given) instead of being frozen here on first sight. A remembered choice that no longer resolves is replaced.
    if (!workspace || chosen.current !== stored || stored === null) return;
    if (workspace.key !== stored) {
      setStored(workspace.key);
      try { localStorage.setItem(storageKey, workspace.key); } catch { /* storage unavailable */ }
    }
  }, [workspace, stored, storageKey]);

  const setWorkspaceKey = useCallback((key: string) => {
    chosen.current = key;
    setStored(key);
    try { localStorage.setItem(storageKey, key); } catch { /* storage unavailable */ }
    if (params.has("ws")) {
      const next = new URLSearchParams(params);
      next.delete("ws");
      setParams(next, { replace: true });
    }
  }, [params, setParams, storageKey]);

  const assistants = useMemo(() => {
    if (!workspace) return [];
    const list = [...workspace.assistants];
    const def = workspace.defaultAssistantKey;
    if (def) list.sort((a, b) => Number(b.key === def) - Number(a.key === def));
    return list;
  }, [workspace]);

  const can = useCallback((permission: string | null | undefined) => {
    if (!permission) return true;
    return permissionMatches(permissions, permission) || permissionMatches(catalog.data?.permissions, permission);
  }, [permissions, catalog.data?.permissions]);

  const registerActions = useCallback((actions: PaletteItem[]) => {
    setActionSets((s) => [...s, actions]);
    return () => setActionSets((s) => s.filter((x) => x !== actions));
  }, []);

  const value = useMemo<WorkspaceContextValue>(() => ({
    catalog: catalog.data,
    loading: catalog.isLoading,
    error: catalog.error,
    workspace,
    workspaceKey: workspace?.key ?? null,
    setWorkspaceKey,
    workspaces,
    assistants,
    defaultAssistant: assistants[0] ?? null,
    can,
    offline: catalog.data?.offline ?? false,
    openPalette: onOpenPalette,
    openShortcuts: onOpenShortcuts,
    registerActions,
    actions: actionSets.flat(),
  }), [catalog.data, catalog.isLoading, catalog.error, workspace, setWorkspaceKey, workspaces, assistants, can, onOpenPalette, onOpenShortcuts, registerActions, actionSets]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within <WorkspaceProvider>");
  return ctx;
}

/** Contributes page-level actions to the command palette while the page is mounted. */
export function usePaletteActions(actions: PaletteItem[]): void {
  const { registerActions } = useWorkspace();
  const ids = actions.map((a) => a.id).join("|");
  useEffect(() => registerActions(actions), [ids, registerActions]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Localised name / description helpers for catalog objects. */
export function localised<T extends { name: string; nameAr?: string | null }>(x: T, locale: string): string {
  return locale === "ar" && x.nameAr ? x.nameAr : x.name;
}

export function localisedDescription(x: { description?: string | null; descriptionAr?: string | null }, locale: string): string | null {
  return (locale === "ar" && x.descriptionAr ? x.descriptionAr : x.description) ?? null;
}
